import { BaseApiClient } from "../api/baseApiClient.service";
import type { JWTPayload } from "../auth/auth.types";
import {
	DEFAULT_PODCAST_LENGTH,
	PODCAST_LENGTH,
} from "../podcast/podcast.types";
import type { PodcastLength } from "../podcast/podcast.types";

export type BaseMedium = {
	id: string;
	title: string;
	authors: string[];
	publicationDate: string | null;
	podWorkspaceId: string;
	podFileId: string;
	createdAt: Date;
	createdBy?: JWTPayload | null;
	createdById?: string;
};

export type ExtractedChapter = {
	title: string;
	pageCount: number;
	textLength: number;
};

export type TextExtractionResult = {
	success: boolean;
	title: string;
	blobLink: string;
	totalChapters: number;
	totalTextLength: number;
	hasInsufficientText: boolean;
	chaptersWithText: number;
	chapters: ExtractedChapter[];
};

/**
 * Service for interacting with the Base Medium API.
 * Mirrors the style of PodWorkspaceService.
 */
export class BaseMediumService {
	static path = "base-medium";

	/**
	 * Get all base mediums for the authenticated user
	 */
	static async getBaseMediumsForUser(): Promise<BaseMedium[]> {
		return BaseApiClient.getInstance().get<BaseMedium[]>(
			`${BaseMediumService.path}/all`,
		);
	}

	/**
	 * Extract text from PDF without creating workspace/basemedium
	 * This is step 1 of the new flow - just extract and show chapters
	 * Expects:
	 * - file: File (required)
	 * - title: string (required)
	 * - onUploadProgress: optional callback for real-time upload progress (0-100)
	 */
	static async extractOnly(
		file: File,
		title: string,
		onUploadProgress?: (progress: number) => void,
	): Promise<TextExtractionResult> {
		return BaseApiClient.getInstance().uploadFile<TextExtractionResult>(
			`${BaseMediumService.path}/extract-only`,
			file,
			{ title },
			{
				timeout: 240000, // 4 minutes - safe for large files on slow connections
				onUploadProgress,
			},
		);
	}

	/**
	 * Create BaseMedium from already-extracted file
	 * This is step 2 of the new flow - create workspace and baseMedium after user approves
	 * Expects:
	 * - blobLink: string (required) - from extractOnly result
	 * - title: string (required)
	 * - podWorkspaceId: string (required)
	 * - podcastLength: string (optional: SHORT, MEDIUM, LONG - default: MEDIUM)
	 * - language: string (optional: de or en or it or ru or es or fr - default: de)
	 */
	static async createFromExtracted(
		blobLink: string,
		title: string,
		podWorkspaceId: string,
		podcastLength?: PodcastLength,
		language?: "de" | "en" | "it" | "ru" | "es" | "fr",
	): Promise<BaseMedium> {
		const podcastLengthString = podcastLength
			? PODCAST_LENGTH[podcastLength]
			: DEFAULT_PODCAST_LENGTH;
		return BaseApiClient.getInstance().post<
			BaseMedium,
			{
				blobLink: string;
				title: string;
				podWorkspaceId: string;
				podcastLength: string;
				language?: string;
			}
		>(`${BaseMediumService.path}/create-from-extracted`, {
			blobLink,
			title,
			podWorkspaceId,
			podcastLength: podcastLengthString,
			language,
		});
	}

	/**
	 * Upload a file for a given pod workspace
	 * Expects:
	 * - file: File (required)
	 * - title: string (required)
	 * - podWorkspaceId: string (required)
	 * - podcastLength: PodcastLength (optional - default: MEDIUM)
	 * - onUploadProgress: optional callback for real-time upload progress (0-100)
	 */
	static async upload(
		file: File,
		title: string,
		podWorkspaceId: string,
		podcastLength: PodcastLength = DEFAULT_PODCAST_LENGTH,
		onUploadProgress?: (progress: number) => void,
	): Promise<BaseMedium> {
		return BaseApiClient.getInstance().uploadFile<BaseMedium>(
			`${BaseMediumService.path}/upload`,
			file,
			{ title, podWorkspaceId, podcastLength },
			{
				timeout: 240000, // 4 minutes - safe for large files on slow connections
				onUploadProgress,
			},
		);
	}
}
